import { useState, useEffect, useCallback, useContext } from "react";
import { Navbar, Container, Nav, NavDropdown, Dropdown } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";

import FeatureRouter from "@/modules/common/components/layout/side-menu/old-navbar/feature-router";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import {
  EnvironmentVariables,
  OrganizationTierTypes,
  PermissionLevel,
  PermissionsEnum,
} from "@/modules/common/types";
import useWindowSize from "@/modules/common/hooks/screen-size.hook";
import MenuIcon from "@mui/icons-material/Menu";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { PathConstants } from "@/routes/constants/index";
import { ChevronDown, ChevronRight } from "lucide-react";
import UploadLeadsModal from "@/modules/upload-leads/components/upload-leads-modal";
import PermissionGuard from "@/modules/common/components/wrappers/permission-guard";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import NotificationBell from "@/modules/common/components/layout/side-menu/components/notification-bell";
import SettingsButton from "@/modules/common/components/layout/side-menu/components/settings-button";

import { useUserAvailability } from "@/modules/common/hooks/user-availability.hook";
import DomainLinks from "@/modules/common/components/layout/side-menu/components/domain-links";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import ToggleButton from "@/modules/common/components/input/toggle-button";
import { NawyPassportContext } from "@/modules/common/context/nawy-passport.provider";
import { userHasPermission } from "@/modules/common/utils";

const isFranchise = `${EnvironmentVariables.IS_FRANCHISE}`;
const erealtyURL = `${EnvironmentVariables.EREALTY_URL}`;

const NavBar = () => {
  const { permissions: currentUserPermissions } =
    useContext(NawyPassportContext);
  const cannotViewPropertyTracks = userHasPermission(
    {
      name: PermissionsEnum.HIDE_PROPERTY_TRACKS,
      level: PermissionLevel.READ,
    },
    currentUserPermissions
  );
  const { selectedOrganization, userTiers } = useOrganization();
  const [logoURL, setLogoURL] = useState("");
  const [toogleNavBar, setToogleNavBar] = useState(false);
  const {
    isAvailable,
    isAutodialerActive,
    toggleUserAvailability,
    logout,
    availabilityData,
  } = useUserAvailability();
  const [isChevronRight, setIsChevronRight] = useState(false);
  const [showAgentsDropdown, setShowAgentsDropdown] = useState(false);
  const [toggleUploadLeadsModal, setToggleUploadLeadsModal] = useState(false);

  const handleToggleClick = () => {
    setShowAgentsDropdown(!showAgentsDropdown);
    setIsChevronRight(!isChevronRight);
  };

  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  const getNavLinkStyles = useCallback(
    (navPath: string) => {
      if (
        location.pathname === navPath ||
        (location.pathname === PathConstants.LEADS &&
          navPath === PathConstants.HOME)
      ) {
        return { color: "white", textDecoration: "none" };
      }

      return {};
    },
    [location.pathname]
  );

  useEffect(() => {
    selectedOrganization?.logo?.link &&
      setLogoURL(selectedOrganization?.logo?.link);
  }, [selectedOrganization]);

  const closeInnerDropdowns = () => {
    if (showAgentsDropdown) {
      setShowAgentsDropdown(false);
      setIsChevronRight(false);
    }
  };

  const showUploadLeadsModal = () => {
    setToggleUploadLeadsModal(!toggleUploadLeadsModal);
  };
  const screenSize = useWindowSize();
  const [isRelative, setIsRelative] = useState(false);

  useEffect(() => {
    if (screenSize?.width && screenSize.width <= 999) {
      setIsRelative(true);
    } else {
      setIsRelative(false);
    }
  }, [screenSize]);

  const screen = useScreenSize();

  const companyName = EnvironmentVariables.COMPANY_NAME.toString();

  return (
    <Navbar
      collapseOnSelect
      expand="lg"
      style={{
        backgroundColor: nawyTheme.nawy.colors.semantic.layer.accentDark,
        overflow: isRelative ? "visible" : "",
        height: toogleNavBar ? "100vh" : "64px",
        position: "fixed", // Always use fixed position to prevent horizontal scrolling
        top: 0,
        left: 0, // Ensure it stays at the left edge
        right: 0, // Ensure it spans the full width
        zIndex: 999,
        display: "flex",
        alignItems: "center",
        minWidth: "100%",
        justifyContent: "center",
        marginBottom: "-60px",
        msOverflowStyle: "none",
        scrollbarWidth: "none",
        paddingBottom: "10px",
      }}
      variant="dark"
    >
      <Container
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          height: toogleNavBar ? "fit-content" : "60px",
          minWidth: "100%",
          padding: "0px 32px",
          fontSize: "12px",
          whiteSpace: "nowrap",
          maxWidth: "100%",
          position: isRelative ? "absolute" : "relative",
        }}
      >
        <style>
          {`
        a {
          color: white;
          text-decoration: none;
        }
    `}
        </style>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",

            width:
              screenSize?.width && screenSize.width < 1000
                ? "100%"
                : "fit-content",
          }}
        >
          <Navbar.Brand
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                maxWidth: "60px",
                maxHeight: "36px",
                marginRight: "30px",
                marginLeft: "10px",
                marginTop: screen.isMobile ? "2px" : "10px",
              }}
            >
              <Nav.Link
                href={PathConstants.HOME}
                style={getNavLinkStyles(PathConstants.HOME)}
                onClick={handleNavigation(PathConstants.HOME)}
              >
                {logoURL ? (
                  <img src={logoURL} alt="logo" width="130px" />
                ) : null}
              </Nav.Link>
            </Box>
          </Navbar.Brand>

          <Box
            sx={{
              display:
                screenSize?.width && screenSize.width >= 1000 ? "none" : "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              cursor: "pointer",
              border: "1px solid #4e4e4e68",
              borderRadius: "4px",
              padding: "4px",
            }}
            onClick={() => {
              setToogleNavBar(!toogleNavBar);
            }}
          >
            <MenuIcon
              sx={{
                width: "30px",
                height: "30px",
              }}
            />
          </Box>
        </Box>

        <Navbar.Collapse
          style={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
          }}
          id="responsive-navbar-nav"
        >
          <Nav
            key={location.pathname}
            style={{
              display:
                screenSize?.width && screenSize.width < 1000 && !toogleNavBar
                  ? "none"
                  : "flex",
              flexDirection:
                screenSize?.width && screenSize.width < 1000 ? "column" : "row",
              backgroundColor:
                window.innerWidth < 1000 ? "#0C314E" : "transparent",
              justifyContent:
                window.innerWidth < 1000 ? "center" : "space-between",
              color: "white",
              textDecoration: "none",
              alignItems: "center",
              height: "100%",
              width: "100%",
            }}
            className="me-auto"
          >
            <Box
              sx={{
                display: "flex",
                flexDirection:
                  screenSize?.width && screenSize.width < 1000
                    ? "column"
                    : "row",
                justifyContent:
                  window.innerWidth < 1000 ? "center" : "space-between",
                textAlign: "center",
                marginTop: "10px",
              }}
            >
              {companyName === OrganizationTierTypes.NAWY && (
                <Nav.Link
                  href={PathConstants.NEW_HOME}
                  style={getNavLinkStyles(PathConstants.NEW_HOME)}
                  onClick={handleNavigation(PathConstants.NEW_HOME)}
                >
                  Home
                </Nav.Link>
              )}

              {companyName === OrganizationTierTypes.NAWY && (
                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.CUSTOMERS,
                      level: PermissionLevel.READ,
                    },
                  ]}
                >
                  <Nav.Link
                    href={PathConstants.CLIENTS}
                    style={getNavLinkStyles(PathConstants.CLIENTS)}
                    onClick={handleNavigation(PathConstants.CLIENTS)}
                  >
                    Clients
                  </Nav.Link>
                </PermissionGuard>
              )}

              <Nav.Link
                href={PathConstants.LEADS}
                style={getNavLinkStyles(PathConstants.LEADS)}
                onClick={handleNavigation(PathConstants.LEADS)}
              >
                Leads
              </Nav.Link>

              {companyName === OrganizationTierTypes.NAWY &&
                !cannotViewPropertyTracks && (
                  <PermissionGuard
                    permissions={[
                      {
                        name: PermissionsEnum.TRACKS,
                        level: PermissionLevel.READ,
                      },
                    ]}
                  >
                    <Nav.Link
                      href={PathConstants.PROPERTY_TRACKS}
                      style={getNavLinkStyles(PathConstants.PROPERTY_TRACKS)}
                      onClick={handleNavigation(PathConstants.PROPERTY_TRACKS)}
                    >
                      Property Tracks
                    </Nav.Link>
                  </PermissionGuard>
                )}

              <Nav.Link
                href={PathConstants.NEXT_ACTIONS}
                style={getNavLinkStyles(PathConstants.NEXT_ACTIONS)}
                onClick={handleNavigation(PathConstants.NEXT_ACTIONS)}
              >
                Next Actions
              </Nav.Link>

              {companyName === OrganizationTierTypes.NAWY && (
                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.NAWY_NOW_CALCULATOR,
                      level: PermissionLevel.READ,
                    },
                    {
                      name: PermissionsEnum.NAWY_UNLOCKED_CALCULATOR,
                      level: PermissionLevel.READ,
                    },
                  ]}
                >
                  <Nav.Link
                    href={PathConstants.NAWY_CALCULATOR}
                    style={getNavLinkStyles(PathConstants.NAWY_CALCULATOR)}
                    onClick={handleNavigation(PathConstants.NAWY_CALCULATOR)}
                  >
                    Nawy Calculator
                  </Nav.Link>
                </PermissionGuard>
              )}

              <Nav.Link
                href={PathConstants.SALES}
                onClick={handleNavigation(PathConstants.SALES)}
                style={getNavLinkStyles(PathConstants.SALES)}
              >
                Sales
              </Nav.Link>

              {companyName === OrganizationTierTypes.NAWY && (
                <FeatureRouter
                  featureKey={"commissions"}
                  element={
                    <Nav.Link
                      href={PathConstants.COMMISSIONS}
                      onClick={handleNavigation(PathConstants.COMMISSIONS)}
                      className="text-center"
                      style={getNavLinkStyles(PathConstants.COMMISSIONS)}
                    >
                      Commissions
                    </Nav.Link>
                  }
                />
              )}
              <Nav.Link
                onClick={handleNavigation(PathConstants.NAWY_INVENTORY)}
                style={getNavLinkStyles(PathConstants.NAWY_INVENTORY)}
                href={PathConstants.NAWY_INVENTORY}
              >
                Inventory
              </Nav.Link>

              {EnvironmentVariables.COMPANY_NAME.toString() ===
                OrganizationTierTypes.NAWY && (
                <FeatureRouter
                  featureKey={"task-dashboard"}
                  element={
                    <Nav.Link
                      onClick={handleNavigation(PathConstants.TASK_DASHBOARD)}
                      className="text-center"
                      style={getNavLinkStyles(PathConstants.TASK_DASHBOARD)}
                      href={PathConstants.TASK_DASHBOARD}
                    >
                      Task Dashboard
                    </Nav.Link>
                  }
                />
              )}

              <NavDropdown
                style={{
                  textAlign: "center",
                  width: screen.isMobile ? "60vw" : "",
                }}
                title="More"
                id="collasible-nav-dropdown1"
                onToggle={closeInnerDropdowns}
              >
                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.AGENTS,
                      level: PermissionLevel.READ,
                    },
                  ]}
                >
                  <NavDropdown
                    title={
                      <span style={{ color: "black" }}>
                        Agents
                        <span className="chevron-icon">
                          {isChevronRight ? (
                            <ChevronRight
                              style={{
                                width: 16,
                                height: 16,
                                marginLeft: "12px",
                              }}
                            />
                          ) : (
                            <ChevronDown
                              style={{
                                width: 16,
                                height: 16,
                                marginLeft: "12px",
                              }}
                            />
                          )}
                        </span>
                      </span>
                    }
                    drop="end"
                    style={{
                      background: isChevronRight ? "#E6F0F4" : "",
                      position: "relative",
                    }}
                    className="text-center agent custom-dropdown"
                    show={showAgentsDropdown}
                    onClick={handleToggleClick}
                  >
                    <style>
                      {`
                        .custom-dropdown .dropdown-menu {
                          left: 95% !important;  /* Position to the right of parent */
                          top: 0 !important;      /* Align with top of parent */
                          transform: translateX(8px) !important; /* Add some spacing */
                        }
                        .agent:hover {
                          background: #F9F9F9;
                        }
                        .agent .dropdown-toggle::after {
                          display: none !important;
                        }
                        .chevron-icon {
                          display: inline-block;
                          transition: transform 0.4s ease-in-out;
                        }
                        .chevron-icon svg {
                          transition: transform 0.4s ease-in-out;
                        }
                        @media (max-width: 999px) {
                        .agent .dropdown-menu {
                          position: absolute !important;
                          left: 20% !important;
                          top: 100% !important;
                          margin-left: 0 !important;
                        }
                      }
                    `}
                    </style>
                    <NavDropdown.Item
                      onClick={handleNavigation(PathConstants.AGENTS_ACTIVITY)}
                      className="text-center"
                      href={PathConstants.AGENTS_ACTIVITY}
                    >
                      Agents Activity
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      onClick={handleNavigation(PathConstants.AGENTS_TARGETS)}
                      className="text-center"
                      href={PathConstants.AGENTS_TARGETS}
                    >
                      Agents Targets
                    </NavDropdown.Item>
                    <NavDropdown.Item
                      onClick={handleNavigation(
                        PathConstants.AUTO_DELEGATION_WEIGHTS
                      )}
                      className="text-center"
                      href={PathConstants.AUTO_DELEGATION_WEIGHTS}
                    >
                      Auto Delegation Weights
                    </NavDropdown.Item>
                  </NavDropdown>
                </PermissionGuard>

                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.DASHBOARDS,
                      level: PermissionLevel.READ,
                    },
                  ]}
                >
                  <NavDropdown.Item
                    onClick={handleNavigation(PathConstants.DASHBOARDS)}
                    className="text-center"
                    href={PathConstants.DASHBOARDS}
                  >
                    Dashboards
                  </NavDropdown.Item>
                </PermissionGuard>

                <FeatureRouter
                  featureKey={"sales_league"}
                  element={
                    <PermissionGuard
                      permissions={[
                        {
                          name: PermissionsEnum.SALES_LEAGUE,
                          level: PermissionLevel.READ,
                        },
                      ]}
                    >
                      <NavDropdown.Item
                        onClick={handleNavigation(PathConstants.LEAGUE)}
                        className="text-center"
                        href={PathConstants.LEAGUE}
                      >
                        Sales League
                      </NavDropdown.Item>
                    </PermissionGuard>
                  }
                />

                <NavDropdown.Item
                  href={erealtyURL}
                  target="_blank"
                  className="text-center"
                >
                  E-Realty
                </NavDropdown.Item>
                <FeatureRouter
                  featureKey={"lead-listing"}
                  element={
                    <NavDropdown.Item
                      onClick={handleNavigation(
                        PathConstants.LISTING_DASHBOARD
                      )}
                      className="text-center"
                      href={PathConstants.LISTING_DASHBOARD}
                    >
                      Listing Dashboard
                    </NavDropdown.Item>
                  }
                />

                {companyName === OrganizationTierTypes.NAWY && (
                  <NavDropdown.Item
                    onClick={handleNavigation(PathConstants.AMBASSADORS)}
                    className="text-center"
                    style={{
                      ...getNavLinkStyles(PathConstants.AMBASSADORS),
                      color: "black",
                    }}
                    href={PathConstants.AMBASSADORS}
                  >
                    Ambassadors
                  </NavDropdown.Item>
                )}

                {companyName === OrganizationTierTypes.NAWY && (
                  <PermissionGuard
                    permissions={[
                      {
                        name: PermissionsEnum.CAN_VIEW_RATINGS_TAB,
                        level: PermissionLevel.READ,
                      },
                    ]}
                  >
                    <NavDropdown.Item
                      onClick={handleNavigation(PathConstants.REVIEWS)}
                      className="text-center"
                      style={{
                        ...getNavLinkStyles(PathConstants.REVIEWS),
                        color: "black",
                      }}
                      href={PathConstants.REVIEWS}
                    >
                      Reviews
                    </NavDropdown.Item>
                  </PermissionGuard>
                )}
                <FeatureRouter
                  featureKey={"lead-icons"}
                  element={
                    <NavDropdown.Item
                      onClick={handleNavigation(PathConstants.LEAD_ICONS)}
                      className="text-center"
                      href={PathConstants.LEAD_ICONS}
                    >
                      Lead Icons
                    </NavDropdown.Item>
                  }
                />

                <FeatureRouter
                  featureKey={"sales-agents-commissions"}
                  element={
                    <PermissionGuard
                      permissions={[
                        {
                          name: PermissionsEnum.AGENTS_COMMISSIONS,
                          level: PermissionLevel.READ,
                        },
                      ]}
                    >
                      <NavDropdown.Item
                        onClick={handleNavigation(
                          PathConstants.SALES_COMMISSIONS
                        )}
                        className="text-center"
                        href={PathConstants.SALES_COMMISSIONS}
                      >
                        Agents Commissions
                      </NavDropdown.Item>
                    </PermissionGuard>
                  }
                />

                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.UPLOAD_LIST_LEADS,
                      level: PermissionLevel.WRITE,
                    },
                  ]}
                >
                  <UploadLeadsModal
                    toggleUploadLeadsModal={toggleUploadLeadsModal}
                    setToggleUploadLeadsModal={setToggleUploadLeadsModal}
                  ></UploadLeadsModal>
                  <NavDropdown.Item
                    onClick={showUploadLeadsModal}
                    className="text-center"
                  >
                    Upload Leads
                  </NavDropdown.Item>
                </PermissionGuard>

                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.DAILY_OPERATIONS,
                      level: PermissionLevel.READ,
                    },
                  ]}
                >
                  <NavDropdown.Item
                    onClick={handleNavigation(PathConstants.DAILY_OPERATIONS)}
                    className="text-center"
                    href={PathConstants.DAILY_OPERATIONS}
                  >
                    Daily Operations
                  </NavDropdown.Item>
                </PermissionGuard>

                {companyName !== OrganizationTierTypes.PARTNERS ? (
                  <PermissionGuard
                    permissions={[
                      {
                        name: PermissionsEnum.SEARCH_COMMENTS,
                        level: PermissionLevel.READ,
                      },
                    ]}
                  >
                    <NavDropdown.Item
                      onClick={handleNavigation(PathConstants.COMMENTS_SEARCH)}
                      className="text-center"
                      href={PathConstants.COMMENTS_SEARCH}
                    >
                      Search Comments
                    </NavDropdown.Item>
                  </PermissionGuard>
                ) : null}
          

                {companyName === OrganizationTierTypes.NAWY ? (
                    <NavDropdown.Item
                      onClick={handleNavigation(PathConstants.EXPO_DASHBOARD)}
                      className="text-center"
                      href={PathConstants.EXPO_DASHBOARD}
                    >
                     Expo Dashboard
                    </NavDropdown.Item>
                ) : null}

                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.AGENTS_TREE,
                      level: PermissionLevel.READ,
                    },
                  ]}
                >
                  <NavDropdown.Item
                    onClick={handleNavigation(PathConstants.AGENTS_TREE)}
                    className="text-center"
                    href={PathConstants.AGENTS_TREE}
                  >
                    Agents Tree
                  </NavDropdown.Item>
                </PermissionGuard>

                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.RENTAL_INVENTORY,
                      level: PermissionLevel.READ,
                    },
                  ]}
                >
                  <NavDropdown.Item
                    onClick={handleNavigation(PathConstants.RENTAL_INVENTORY)}
                    className="text-center"
                    href={PathConstants.RENTAL_INVENTORY}
                  >
                    Rental Inventory
                  </NavDropdown.Item>
                </PermissionGuard>
              </NavDropdown>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                <NavDropdown
                  style={{
                    textAlign: "center",
                    width: "100%",
                  }}
                  title="Help"
                  id="collasible-nav-dropdown1"
                >
                  <NavDropdown.Item
                    onClick={handleNavigation(PathConstants.MARKET_UPDATES)}
                    className="text-center"
                    href={PathConstants.MARKET_UPDATES}
                  >
                    Market Updates
                  </NavDropdown.Item>
                  <NavDropdown.Item
                    onClick={handleNavigation(PathConstants.STATUSES_GLOSSARY)}
                    className="text-center"
                    href={PathConstants.STATUSES_GLOSSARY}
                  >
                    Statuses Glossary
                  </NavDropdown.Item>
                </NavDropdown>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent:
                  window.innerWidth < 1000 ? "center" : "space-between",
                flexDirection: window.innerWidth > 1000 ? "row" : "column",
                color: "white",
                textDecoration: "none",
                height: "100%",
                width:
                  screenSize?.width && screenSize.width < 1000
                    ? "100%"
                    : "fit-content",
                marginRight: window.innerWidth > 1000 ? "90px" : "",
                marginTop: "10px",
              }}
            >
              <NotificationBell
                variant={"icon"}
                href={PathConstants.NOTIFICATIONS_CENTER}
              />

              <style>
                {`
    .settings-toggle::after {
      display: none !important;
    }
  `}
              </style>

              <Nav
                style={{
                  textAlign: "center",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: screen.isMobile ? "10px" : "25px",
                }}
              >
                <PermissionGuard
                  permissions={[
                    {
                      name: PermissionsEnum.SETTINGS_TAB,
                      level: PermissionLevel.READ,
                    },
                  ]}
                >
                  <Dropdown
                    align="end"
                    style={{ textAlign: "center", position: "relative" }}
                  >
                    <Dropdown.Toggle
                      as="div"
                      className={screen.isMobile ? "" : "settings-toggle"}
                    >
                      <SettingsButton
                        variant={screen.isMobile ? "text" : "icon"}
                      />
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {/* Common Settings */}
                      <PermissionGuard
                        permissions={[
                          {
                            name: PermissionsEnum.COMMON_SETTINGS,
                            level: PermissionLevel.READ,
                          },
                        ]}
                      >
                        <>
                          <Dropdown.Item
                            onClick={handleNavigation(PathConstants.STATUSES)}
                            className="text-center"
                            href={PathConstants.STATUSES}
                          >
                            Statuses
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={handleNavigation(PathConstants.CATEGORIES)}
                            className="text-center"
                            href={PathConstants.CATEGORIES}
                          >
                            Categories
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={handleNavigation(PathConstants.POOLS)}
                            className="text-center"
                            href={PathConstants.POOLS}
                          >
                            Pools
                          </Dropdown.Item>
                          <Dropdown.Item
                            onClick={handleNavigation(PathConstants.FUNNELS)}
                            className="text-center"
                            href={PathConstants.FUNNELS}
                          >
                            Funnels
                          </Dropdown.Item>

                          <FeatureRouter
                            featureKey="lead-icons"
                            element={
                              <Dropdown.Item
                                onClick={handleNavigation(
                                  PathConstants.LEAD_ICONS
                                )}
                                className="text-center"
                                href={PathConstants.LEAD_ICONS}
                              >
                                Lead Icons
                              </Dropdown.Item>
                            }
                          />

                          <PermissionGuard
                            permissions={[
                              {
                                name: PermissionsEnum.LEAD_TAGS,
                                level: PermissionLevel.READ,
                              },
                            ]}
                          >
                            <Dropdown.Item
                              onClick={handleNavigation(
                                PathConstants.LEAD_TAGS
                              )}
                              className="text-center"
                              href={PathConstants.LEAD_TAGS}
                            >
                              Lead Tags
                            </Dropdown.Item>
                          </PermissionGuard>

                          <Dropdown.Item
                            onClick={handleNavigation(
                              PathConstants.CONFIGURATIONS
                            )}
                            className="text-center"
                            href={PathConstants.CONFIGURATIONS}
                          >
                            Configurations
                          </Dropdown.Item>

                          <Dropdown.Item
                            onClick={handleNavigation(
                              PathConstants.AI_CONFIGURATIONS
                            )}
                            className="text-center"
                            href={PathConstants.AI_CONFIGURATIONS}
                          >
                            AI Configurations
                          </Dropdown.Item>

                          <FeatureRouter
                            featureKey="payment_plans"
                            element={
                              <Dropdown.Item
                                onClick={handleNavigation(
                                  PathConstants.PAYMENT_PLANS
                                )}
                                className="text-center"
                                href={PathConstants.PAYMENT_PLANS}
                              >
                                Payment Plans
                              </Dropdown.Item>
                            }
                          />
                        </>
                      </PermissionGuard>

                      {/* Target Configurations */}
                      <PermissionGuard
                        permissions={[
                          {
                            name: PermissionsEnum.TARGETS_CONFIGURATIONS_PAGE,
                            level: PermissionLevel.READ,
                          },
                        ]}
                      >
                        <Dropdown.Item
                          onClick={handleNavigation(
                            PathConstants.DISPLAY_ORGANIZATIONS_TARGETS
                          )}
                          className="text-center"
                          href={PathConstants.DISPLAY_ORGANIZATIONS_TARGETS}
                        >
                          Target Configurations
                        </Dropdown.Item>
                      </PermissionGuard>

                      {/* Auto Assignment Pools */}
                      <Dropdown.Item
                        onClick={handleNavigation(
                          PathConstants.AUTO_ASSIGNMENT_POOLS
                        )}
                        className="text-center"
                        href={PathConstants.AUTO_ASSIGNMENT_POOLS}
                      >
                        Auto Assignment Pools
                      </Dropdown.Item>

                      {/* Facebook Rules */}
                      <PermissionGuard
                        permissions={[
                          {
                            name: PermissionsEnum.FACEBOOK_RULES,
                            level: PermissionLevel.READ,
                          },
                        ]}
                      >
                        <Dropdown.Item
                          onClick={handleNavigation(
                            PathConstants.FACEBOOK_RULES
                          )}
                          className="text-center"
                          href={PathConstants.FACEBOOK_RULES}
                        >
                          Facebook Rules
                        </Dropdown.Item>
                      </PermissionGuard>

                      {/* Users */}
                      <PermissionGuard
                        permissions={[
                          {
                            name: PermissionsEnum.USERS_PAGE,
                            level: PermissionLevel.READ,
                          },
                        ]}
                      >
                        <Dropdown.Item
                          onClick={handleNavigation(PathConstants.USERS)}
                          className="text-center"
                          href={PathConstants.USERS}
                        >
                          Users
                        </Dropdown.Item>
                      </PermissionGuard>

                        <Dropdown.Item
                          onClick={handleNavigation(
                            PathConstants.SETTINGS
                          )}
                          className="text-center"
                          href={PathConstants.SETTINGS}
                        >
                         Settings
                        </Dropdown.Item>
                      

                      {/* Company Specific */}
                      {companyName === OrganizationTierTypes.NAWY && (
                        <Dropdown.Item
                          onClick={handleNavigation(
                            PathConstants.PROPERTY_TRACK_SETTINGS
                          )}
                          className="text-center"
                          href={PathConstants.PROPERTY_TRACK_SETTINGS}
                        >
                          Property Tracker
                        </Dropdown.Item>
                      )}
                      {EnvironmentVariables.COMPANY_NAME.toString() ===
                        OrganizationTierTypes.NAWY && (
                        <Dropdown.Item
                          onClick={handleNavigation(
                            PathConstants.INTERESTS_CONFIGURATION
                          )}
                          className="text-center"
                          href={PathConstants.INTERESTS_CONFIGURATION}
                        >
                          Interests
                        </Dropdown.Item>
                      )}
                    </Dropdown.Menu>
                  </Dropdown>
                </PermissionGuard>
              </Nav>

              <NavDropdown
                style={{ color: "white" }}
                title={availabilityData?.data?.user?.name}
                id="collasible-nav-dropdown3"
              >
                {isFranchise === "true" && (
                  <>
                    <NavDropdown.Item
                      onClick={handleNavigation("/reset-password")}
                      className="text-center"
                      style={{
                        color: "black",
                      }}
                    >
                      Change password
                    </NavDropdown.Item>
                    <Divider sx={{ margin: "8px" }} />
                  </>
                )}

                <Box
                  sx={{
                    padding: "8px 16px",
                  }}
                >
                  <DomainLinks userTiers={userTiers} />
                </Box>

                <Divider sx={{ margin: "8px" }} />

                <Box
                  sx={{
                    minWidth: 240,
                    padding: "8px 16px",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography sx={{ fontSize: 14, lineHeight: "20px" }}>
                      Available
                    </Typography>

                    <Box display="flex" alignItems="center" gap={1}>
                      {isAutodialerActive && (
                        <Box
                          sx={{
                            fontSize: 11,
                            padding: "2px 8px",
                            borderRadius: 1,
                            color: "#015C9A",
                            border: "1px solid #015C9A",
                            lineHeight: "16px",
                          }}
                        >
                          Autodialer
                        </Box>
                      )}

                      <Tooltip
                        title={
                          isAutodialerActive
                            ? "Can't change availability while Autodialer is connected"
                            : ""
                        }
                        placement="top"
                        arrow
                      >
                        <span>
                          <ToggleButton
                            checked={isAvailable || isAutodialerActive}
                            onChange={(e) => {
                              if (!isAutodialerActive) {
                                toggleUserAvailability(e.target.checked);
                              }
                            }}
                            disabled={isAutodialerActive}
                            size="small"
                            data-testid="availability-switch"
                          />
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </Box>

                <Divider sx={{ margin: "8px" }} />

                <NavDropdown.Item
                  onClick={() => logout()}
                  style={{
                    padding: "8px 16px",
                    fontSize: "14px",
                    color: "#C72D22",
                  }}
                >
                  Log out
                </NavDropdown.Item>
              </NavDropdown>
            </Box>
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavBar;
